<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from 'vue'

import type { ResolvedDataVisibleTrend } from '../composables/useDataVisibleReportRequest'
import type { ChartType, ReportViewType } from '../models/chart.types'
import type { ReportColumn } from '../models/column.types'
import type {
  DataVisibleCardMediaType,
  DataVisibleCellSlotProps,
} from '../models/dataVisible.types'
import type { ReportFilter } from '../models/filter.types'
import type { ReportRow } from '../models/report.types'
import type { ResolvedReportTableConfig } from '../models/tableConfig.types'
import type { ReportTableType } from '../models/tablePresentation.types'
import type { ChartSpec } from '../service/chart/buildChartOptions'
import { chartLoadingShape } from '../service/chart/chartLoadingShape'

import ReportMediaCard from './card/ReportMediaCard.vue'
import ChartRenderer from './chart/ChartRenderer.vue'
import Ga4Indicator from './Ga4Indicator.vue'
import ReportTablePresenter from './table/ReportTablePresenter.vue'
import ReportTableSkeleton from './table/ReportTableSkeleton.vue'
import TrendTable from './table/TrendTable.vue'
import type { ReportPaginationProps } from './table/types'

import { useI18n } from '@/shared/i18n'
import { Button, type SortState, type TableColumn } from '@/shared/ui'
import type { ChartMenuItem } from '@/shared/ui/chart'
import type { UiAsyncState } from '@/shared/ui/types'

// Replaces the click-through pager (ORP-scroll-pagination): the table/card grid gets a bounded
// viewport and loads its next server batch as the user scrolls near the bottom, mirroring v3
// `OrphexReportTable.vue`'s `handleScroll` — see `DataTable.vue`'s `onScroll`/`load-more`.
const SCROLL_VIEWPORT_MAX_HEIGHT = 'min(700px, 70vh)'
const CARD_LOAD_MORE_THRESHOLD = 48

const props = withDefaults(
  defineProps<{
    view: ReportViewType
    viewUnavailable?: boolean
    tableUnavailable?: boolean
    chartCatalogLoading?: boolean
    chartUnavailable?: boolean
    chartContractValid?: boolean
    state: UiAsyncState
    /** A refetch is in flight while the previously rendered rows are still shown. */
    refreshing?: boolean
    rows: ReportRow[]
    columns: ReportColumn[]
    tableConfig: ResolvedReportTableConfig
    tableSort: SortState
    tableType: ReportTableType
    enableFreeze?: boolean
    breakdownField?: string | null
    matrixDimension?: string | null
    pivotMetric?: string | null
    total?: Record<string, unknown> | null
    totalPeriod?: Record<string, unknown> | null
    otherTotal?: Record<string, unknown> | null
    grandTotal?: Record<string, unknown> | null
    ga4Total?: Record<string, unknown> | null
    totalRowKinds?: readonly ('other' | 'ga4')[]
    heatmap?: boolean | readonly string[]
    chartMenuItems?: readonly ChartMenuItem[]
    comparisonExpandableFields?: readonly string[]
    comparisonExpandedFields?: readonly string[]
    trend?: ResolvedDataVisibleTrend | null
    pagination?: ReportPaginationProps | null
    cardMediaType?: DataVisibleCardMediaType
    cardDimensionFields?: readonly string[]
    cardMetricFields?: readonly string[]
    filterable?: boolean
    chartSpec: ChartSpec | null
    channel?: string
    /** Selected chart type — known before the response, so the skeleton matches what will render. */
    chartType?: ChartType | null
    ariaLabel: string
    emptyLabel: string
    errorLabel: string
    retryLabel: string
  }>(),
  {
    viewUnavailable: false,
    tableUnavailable: false,
    enableFreeze: false,
    chartCatalogLoading: false,
    chartUnavailable: false,
    chartContractValid: true,
    refreshing: false,
    breakdownField: null,
    matrixDimension: null,
    pivotMetric: null,
    total: null,
    totalPeriod: null,
    otherTotal: null,
    grandTotal: null,
    ga4Total: null,
    totalRowKinds: () => ['other', 'ga4'],
    heatmap: false,
    trend: null,
    pagination: null,
    channel: '',
    cardMediaType: 'Image',
    filterable: false,
    chartType: null,
    chartMenuItems: () => [],
    comparisonExpandableFields: () => [],
    comparisonExpandedFields: () => [],
  },
)

const emit = defineEmits<{
  reload: []
  sort: [value: SortState]
  reorder: [value: TableColumn<ReportRow>[]]
  resize: [value: TableColumn<ReportRow>[]]
  freeze: [field: string, frozen: boolean]
  'comparison-expand': [field: string, expanded: boolean]
  'load-more': []
  'add-filter': [filter: ReportFilter]
}>()
defineSlots<{
  cell?: (props: DataVisibleCellSlotProps) => unknown
  cohort?: () => unknown
}>()
const { t } = useI18n()

function declaredCardColumns(fields: readonly string[]): ReportColumn[] {
  const byField = new Map(props.columns.map((column) => [column.field, column] as const))
  return fields
    .map((field) => byField.get(field))
    .filter((column): column is ReportColumn => column !== undefined)
    .map((column) => ({ ...column, invisible: false }))
}

const cardDimensions = computed(() =>
  props.cardDimensionFields?.length
    ? declaredCardColumns(props.cardDimensionFields)
    : props.columns.filter((column) => !column.numeric && !column.invisible),
)
const cardMetrics = computed(() =>
  props.cardMetricFields?.length
    ? declaredCardColumns(props.cardMetricFields)
    : props.columns.filter((column) => column.numeric && !column.invisible),
)

const totalRows = computed(() => props.pagination?.total ?? null)
// The card grid has no built-in windowing (unlike DataTable), so this mirrors DataTable's own
// scroll gate directly: request the next batch once per row count, then wait for `rows` to grow
// before arming again — otherwise every scroll tick past the threshold re-fires `load-more`.
let cardLoadRequestedForRowCount = -1
function requestCardLoadMoreIfExhausted(): void {
  if (totalRows.value === null || props.rows.length >= totalRows.value) return
  if (cardLoadRequestedForRowCount === props.rows.length) return
  cardLoadRequestedForRowCount = props.rows.length
  emit('load-more')
}
function onCardScroll(event: Event): void {
  const target = event.currentTarget as HTMLElement
  if (target.scrollHeight - target.scrollTop - target.clientHeight > CARD_LOAD_MORE_THRESHOLD)
    return
  requestCardLoadMoreIfExhausted()
}

const cardScroll = ref<HTMLElement | null>(null)
// A short opening batch (e.g. a controlled `pagination` of 10 cards) can leave the grid with
// nothing to overflow, so it never scrolls and `onCardScroll` never runs. Recheck after every
// render that could change its height and keep asking for the next batch until it overflows or
// the server says there is no more.
async function fillCardViewportIfShort(): Promise<void> {
  await nextTick()
  const el = cardScroll.value
  if (!el || el.scrollHeight - el.clientHeight > CARD_LOAD_MORE_THRESHOLD) return
  requestCardLoadMoreIfExhausted()
}
onMounted(fillCardViewportIfShort)
watch(() => props.rows.length, fillCardViewportIfShort)
// The card grid's wrapper only exists once `view` is 'card' — `onMounted` alone misses a later
// switch into card view, since the component instance itself was already mounted on 'table'.
watch(() => props.view, fillCardViewportIfShort)

const loadingShape = computed(() => chartLoadingShape(props.chartType))
// Metrics whose values came from GA4 data: the legend marks them, this names them (ORP-25673).
const ga4ChartLabel = computed(() => {
  const spec = props.chartSpec
  return (spec?.ga4Fields ?? [])
    .map((field) => spec?.columns?.[field]?.header ?? spec?.metricLabel(field) ?? field)
    .join(', ')
})
</script>

<template>
  <div v-if="view === 'cohort'" class="data-visible-content__cohort min-w-0">
    <slot name="cohort" />
  </div>
  <div
    v-else-if="viewUnavailable"
    class="data-visible-content__state flex min-h-32 items-center justify-center gap-sm text-muted"
  >
    {{ t('reports.views.unavailable') }}
  </div>
  <div
    v-else-if="view === 'table' && tableUnavailable"
    class="data-visible-content__state flex min-h-32 items-center justify-center gap-sm text-muted"
  >
    {{ t('reports.table.unavailable') }}
  </div>
  <template v-else-if="view === 'table'">
    <!-- v3 mounted no table until the first rows arrived, whatever the columns: the cold load is
         `TableSkeleton`'s placeholder rows + pager, ported as `ReportTableSkeleton`. A refetch over
         rendered rows stays on the presenter's `refreshing` overlay. -->
    <div v-if="state === 'loading'" :aria-label="ariaLabel" aria-busy="true" role="status">
      <ReportTableSkeleton />
    </div>
    <TrendTable
      v-else-if="trend"
      :data="rows"
      :columns="columns"
      :breakdown-column="trend.breakdownColumn"
      :preview-column="trend.previewColumn"
      :date-dimensions="trend.dateDimensions"
      :frequency="trend.frequency"
      :channel="channel"
      v-bind="{ ariaLabel, ...(totalRows === null ? {} : { totalRows }) }"
      :state="state"
      :refreshing="refreshing"
      :max-height="SCROLL_VIEWPORT_MAX_HEIGHT"
      @load-more="emit('load-more')"
    >
      <template #empty>{{ emptyLabel }}</template>
      <template #error>
        <div
          class="data-visible-content__state flex min-h-32 items-center justify-center gap-sm text-muted"
        >
          <span>{{ errorLabel }}</span>
          <Button size="sm" variant="outline" @click="emit('reload')">{{ retryLabel }}</Button>
        </div>
      </template>
    </TrendTable>
    <ReportTablePresenter
      v-else
      v-bind="{ ariaLabel }"
      :rows="rows"
      :columns="columns"
      :state="state"
      :refreshing="refreshing"
      :sort="tableSort"
      :draggable="tableConfig.allowColumnReorder"
      :sort-cycle="tableConfig.sortCycle"
      :initial-sort-direction="tableConfig.initialSortDirection"
      :allow-column-resize="tableConfig.allowColumnResize"
      :table-type="tableType"
      :enable-freeze="enableFreeze"
      :breakdown-field="breakdownField"
      :matrix-dimension="matrixDimension"
      :pivot-metric="pivotMetric"
      :total="total"
      :other-total="otherTotal"
      :grand-total="grandTotal"
      :ga4-total="ga4Total"
      :total-row-kinds="totalRowKinds"
      :heatmap="heatmap"
      :channel="channel"
      :comparison-expandable-fields="comparisonExpandableFields"
      :comparison-expanded-fields="comparisonExpandedFields"
      :max-height="SCROLL_VIEWPORT_MAX_HEIGHT"
      :total-rows="totalRows"
      :filterable="filterable"
      @update:sort="emit('sort', $event)"
      @reorder="emit('reorder', $event)"
      @resize="emit('resize', $event)"
      @freeze="(field: string, frozen: boolean) => emit('freeze', field, frozen)"
      @comparison-expand="
        (field: string, expanded: boolean) => emit('comparison-expand', field, expanded)
      "
      @load-more="emit('load-more')"
      @add-filter="emit('add-filter', $event)"
    >
      <template v-if="$slots.cell" #cell="slotProps">
        <slot name="cell" v-bind="slotProps" />
      </template>
      <template #empty>{{ emptyLabel }}</template>
      <template #error>
        <div
          class="data-visible-content__state flex min-h-32 items-center justify-center gap-sm text-muted"
        >
          <span>{{ errorLabel }}</span>
          <Button size="sm" variant="outline" @click="emit('reload')">{{ retryLabel }}</Button>
        </div>
      </template>
    </ReportTablePresenter>
  </template>
  <template v-else-if="view === 'card'">
    <div
      ref="cardScroll"
      class="data-visible-content__card-scroll overflow-y-auto"
      :style="{ maxHeight: SCROLL_VIEWPORT_MAX_HEIGHT }"
      @scroll.passive="onCardScroll"
    >
      <ReportMediaCard
        :rows="rows"
        :dimensions="cardDimensions"
        :metrics="cardMetrics"
        :state="state"
        :channel="channel"
        :media-type="cardMediaType"
        v-bind="{ ariaLabel, emptyLabel, errorLabel, retryLabel }"
        :unavailable-label="t('reports.card.mediaUnavailable')"
        :filterable="filterable"
        :refreshing="refreshing"
        @reload="emit('reload')"
        @add-filter="emit('add-filter', $event)"
      />
    </div>
  </template>
  <div v-else class="data-visible-content__chart flex h-max lg:h-140 w-full flex-col">
    <!--
      Both pending gates render the chart's own loading silhouette: the plot-area shape belongs to
      `ChartRoot`'s `state="loading"` branch (contract 29 §3.3 rule 1), and a `null` spec keeps the
      Highcharts chunk unloaded while the catalog or the response is still in flight.

      `flex-1` on every `ChartRenderer` (rather than `ChartRoot`'s own `h-full`) is load-bearing:
      this wrapper is a plain flex column with only a MIN height, and a percentage height never
      resolves against an indefinite one — the plot silhouette rendered short and its bars
      accordingly thin before `flex-1` (flex-grow) sidestepped the percentage entirely.
    -->
    <ChartRenderer
      v-if="chartCatalogLoading"
      class="flex-1"
      :spec="null"
      state="loading"
      :loading-shape="loadingShape"
    />
    <div
      v-else-if="chartUnavailable"
      class="data-visible-content__state flex min-h-32 items-center justify-center gap-sm text-muted"
    >
      {{ t('reports.chartTypes.unavailable') }}
    </div>
    <div
      v-else-if="!chartContractValid"
      class="data-visible-content__state flex min-h-32 items-center justify-center gap-sm text-muted"
    >
      {{ t('reports.chartTypes.contractInvalid') }}
    </div>
    <ChartRenderer
      v-else-if="state === 'loading'"
      class="flex-1"
      :spec="null"
      state="loading"
      :loading-shape="loadingShape"
    />
    <div
      v-else-if="state === 'empty'"
      class="data-visible-content__state flex min-h-32 items-center justify-center gap-sm text-muted"
    >
      {{ emptyLabel }}
    </div>
    <div
      v-else-if="state === 'error'"
      class="data-visible-content__state flex min-h-32 items-center justify-center gap-sm text-muted"
    >
      <span>{{ errorLabel }}</span>
      <Button size="sm" variant="outline" @click="emit('reload')">{{ retryLabel }}</Button>
    </div>
    <ChartRenderer
      v-else
      class="flex-1"
      :spec="chartSpec"
      :refreshing="refreshing"
      :menu-items="chartMenuItems"
      :load-error-message="errorLabel"
      :retry-label="retryLabel"
    />
    <div
      v-if="ga4ChartLabel && state === 'ready'"
      class="data-visible-content__ga4 flex justify-end px-sm pt-2xs text-xs text-muted"
    >
      <Ga4Indicator :label="ga4ChartLabel">{{ t('reports.ga4.title') }}</Ga4Indicator>
    </div>
  </div>
</template>
